import moment, {Moment} from "moment"

export type BreakTimeMode = "set" | "open"
export type BreakTimeType = "am" | "pm" | "fixed" | null

export enum BreakEventType {
    Break = "break",
    Lunch = "lunch"
}

export type BreakTimeTime = {
    hour: number
    minute: number
}

export type BreakTime = {
    mode: BreakTimeMode
    type: BreakTimeType
    breakEventType: BreakEventType
    fromTime: BreakTimeTime | null
    toTime: BreakTimeTime | null
    maxMinutes: number | null
}

export type BreakTimeForm = {
    mode: BreakTimeMode
    type: BreakTimeType
    breakEventType: BreakEventType
    fromTime?: Moment
    toTime?: Moment
    maxMinutes?: number | string
}

export type WorkDay = {
    dayOfWeek: number
    name: string
    enabled: boolean
    allDay: boolean
    startTime?: Moment
    endTime?: Moment
    maxHoursEnabled?: boolean
    bonusMinutesEnabled?: boolean
    distEdEnabled?: boolean
    maxHours?: number
    bonusMinutesAfter?: number
    bonusMinutesBefore?: number
    breakTimes?: BreakTimeForm[]
}

export const timeOfDayToMoment = (time: BreakTimeTime | null | undefined): Moment | undefined => {
    if (!time) return undefined
    const todayStr = moment().format("YYYY-MM-DD")
    return moment(todayStr).hour(time.hour).minute(time.minute)
}

export const momentToTimeOfDay = (m: Moment | undefined | null): BreakTimeTime | null => {
    if (!m) return null
    return {hour: m.hour(), minute: m.minute()}
}

export const createDefaultBreakTime = (mode: BreakTimeMode = "open"): BreakTimeForm => ({
    mode,
    type: mode === "open" ? null : "am",
    breakEventType: BreakEventType.Break,
    fromTime: undefined,
    toTime: undefined,
    maxMinutes: undefined
})

export const cloneBreakTimes = (breakTimes: BreakTimeForm[] = []): BreakTimeForm[] =>
    breakTimes.map((bt) => ({
        ...bt,
        fromTime: bt.fromTime?.clone(),
        toTime: bt.toTime?.clone()
    }))

export const parseBreakTimeMaxMinutes = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") {
        return null
    }

    const parsed = typeof value === "number" ? value : Number(value)
    return Number.isNaN(parsed) ? null : parsed
}

export const isBreakTimeMaxMinutesRequiredMissing = (maxMinutes: unknown): boolean =>
    parseBreakTimeMaxMinutes(maxMinutes) === null

export type BreakTimeFieldErrors = {
    maxMinutes?: boolean
    fromTime?: boolean
    toTime?: boolean
}

export type BreakTimeValidationDetail = {
    dayName: string
    dayOfWeek: number
    breakTimeIndex: number
    fields: Array<keyof BreakTimeFieldErrors>
}

export const getBreakTimeFieldErrors = (breakTime: BreakTimeForm): BreakTimeFieldErrors => {
    const errors: BreakTimeFieldErrors = {}

    if (isBreakTimeMaxMinutesRequiredMissing(breakTime.maxMinutes)) {
        errors.maxMinutes = true
    }

    if (breakTime.mode === "set" && breakTime.type === "fixed") {
        if (!breakTime.fromTime) {
            errors.fromTime = true
        }
        if (!breakTime.toTime) {
            errors.toTime = true
        }
    }

    return errors
}

export const hasBreakTimeFieldErrors = (errors?: BreakTimeFieldErrors): boolean =>
    !!(errors?.maxMinutes || errors?.fromTime || errors?.toTime)

export const formatBreakTimeFieldErrorLabel = (fields: Array<keyof BreakTimeFieldErrors>): string => {
    const labels: string[] = []
    if (fields.includes("fromTime")) labels.push("From")
    if (fields.includes("toTime")) labels.push("To")
    if (fields.includes("maxMinutes")) labels.push("Max Minutes")
    return labels.join(", ")
}

/** dayOfWeek -> breakTimeIndex -> field errors */
export type BreakTimesFieldErrorsByDay = Record<number, Record<number, BreakTimeFieldErrors>>

export const collectBreakTimesValidationErrors = (
    workWeek: WorkDay[]
): {byDay: BreakTimesFieldErrorsByDay; details: BreakTimeValidationDetail[]} => {
    const byDay: BreakTimesFieldErrorsByDay = {}
    const details: BreakTimeValidationDetail[] = []

    for (const day of workWeek) {
        if (!day.enabled) continue
        for (let index = 0; index < (day.breakTimes || []).length; index++) {
            const fieldErrors = getBreakTimeFieldErrors(day.breakTimes[index])
            if (!hasBreakTimeFieldErrors(fieldErrors)) continue

            if (!byDay[day.dayOfWeek]) {
                byDay[day.dayOfWeek] = {}
            }
            byDay[day.dayOfWeek][index] = fieldErrors
            details.push({
                dayName: day.name,
                dayOfWeek: day.dayOfWeek,
                breakTimeIndex: index,
                fields: Object.keys(fieldErrors) as Array<keyof BreakTimeFieldErrors>
            })
        }
    }

    return {byDay, details}
}

export const breakTimeFormToPayload = (bt: BreakTimeForm): BreakTime => ({
    mode: bt.mode,
    type: bt.mode === "open" ? null : bt.type,
    breakEventType: bt.breakEventType || BreakEventType.Break,
    fromTime: bt.mode === "set" && bt.type === "fixed" ? momentToTimeOfDay(bt.fromTime) : null,
    toTime: bt.mode === "set" && bt.type === "fixed" ? momentToTimeOfDay(bt.toTime) : null,
    maxMinutes: parseBreakTimeMaxMinutes(bt.maxMinutes)
})

export const breakTimePayloadToForm = (bt: BreakTime): BreakTimeForm => {
    // Legacy API used mode "fixed" for what is now "set"
    const mode = ((bt.mode as string) === "fixed" ? "set" : bt.mode) as BreakTimeMode
    const type = ((bt.type as string) === "manual" ? "fixed" : bt.type) as BreakTimeType
    const showTimes = mode === "set" && type === "fixed"

    return {
        mode,
        type,
        breakEventType: bt.breakEventType || BreakEventType.Break,
        fromTime: showTimes ? timeOfDayToMoment(bt.fromTime) : undefined,
        toTime: showTimes ? timeOfDayToMoment(bt.toTime) : undefined,
        maxMinutes: bt.maxMinutes ?? undefined
    }
}

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const INITIAL_WORK_WEEK: WorkDay[] = WEEKDAY_NAMES.map((name, index) => {
    return {
        dayOfWeek: index,
        name,
        enabled: ![0, 6].includes(index),
        allDay: false
    }
})

/** Reorder work days so the week starts on startWeekOn (0=Sun, 1=Mon, 6=Sat). */
export function orderWorkWeekByStartDay(workWeek: WorkDay[], startWeekOn: number | string = 0): WorkDay[] {
    const parsed = Number(startWeekOn)
    const weekStart = Number.isNaN(parsed) ? 0 : ((parsed % 7) + 7) % 7
    return [...workWeek].sort((a, b) => ((a.dayOfWeek - weekStart + 7) % 7) - ((b.dayOfWeek - weekStart + 7) % 7))
}

export {INITIAL_WORK_WEEK, WEEKDAY_NAMES}
